// Телеметрия клиентских ошибок: всё, что упало в браузере, уезжает на сервер
// и попадает в journald рядом с логами самой панели.
//
// Зачем: серверные логи при белом экране чистые — просто потому, что падает
// клиент, а до сих пор он падал молча. Разбирать такое по телефону через
// «покажи скриншот» невозможно, а удалённой консоли у нас нет.
//
// Правила, без которых репортер сам становится проблемой:
//   * он никогда не бросает — любая его строка обёрнута в try/catch, иначе
//     ошибка внутри обработчика ошибок уходит в бесконечный цикл;
//   * он дедуплицирует — один и тот же message+stack из тика анимации или из
//     цикла реконнекта иначе зальёт диск за минуту;
//   * он ограничен сверху — не больше MAX_REPORTS отчётов на загрузку страницы.

import type { ClientError } from '../protocol'

declare global {
  interface Window {
    /** Копилка инлайнового скрипта из index.html: ошибки, случившиеся до
     *  загрузки этого модуля. Форма та же, что у ClientError. */
    __abEarlyErrors?: ClientError[]
    /** Дубль resetAndReload() из index.html — на случай, если бандл не доехал. */
    __abResetCache?: () => void
  }
}

/** Куда шлём. Тот же origin, что и панель, — без CORS и без токена. */
const ENDPOINT = '/api/client-errors'

/** Потолок на загрузку страницы: дальше молчим совсем. */
const MAX_REPORTS = 20
/** Сколько ждать соседние ошибки, чтобы уехать одной пачкой. */
const BATCH_DELAY_MS = 400
/** Обрезки те же, что на сервере: незачем гонять по сети то, что он отрежет. */
const MAX_MESSAGE = 2000
const MAX_STACK = 8000

let started = false
let sent = 0
const seen = new Set<string>()
let pending: ClientError[] = []
let timer: number | null = null

function cut(value: unknown, limit: number): string {
  const text = typeof value === 'string' ? value : String(value)
  return text.length > limit ? text.slice(0, limit) : text
}

/** Достать message/stack из чего угодно: reject(...) бросают и строками. */
function describe(error: unknown): { message: string; stack: string | null } {
  if (error instanceof Error) {
    return { message: error.message || error.name, stack: error.stack ?? null }
  }
  if (error && typeof error === 'object') {
    const bag = error as { message?: unknown; stack?: unknown }
    if (typeof bag.message === 'string') {
      return {
        message: bag.message,
        stack: typeof bag.stack === 'string' ? bag.stack : null,
      }
    }
    try {
      return { message: JSON.stringify(error), stack: null }
    } catch {
      return { message: 'необъяснимый объект в ошибке', stack: null }
    }
  }
  return { message: String(error), stack: null }
}

function flush(): void {
  try {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    if (pending.length === 0) return
    const body = JSON.stringify({ items: pending })
    pending = []
    // sendBeacon переживает закрытие вкладки — а закрытие вкладки как раз
    // и есть обычная реакция человека на сломанный экран.
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' })
      if (navigator.sendBeacon(ENDPOINT, blob)) return
    }
    void fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'same-origin',
    }).catch(() => {
      // сервер недоступен — это ровно тот случай, когда помочь уже нечем
    })
  } catch {
    // репортер не имеет права ронять приложение, которое он же и чинит
  }
}

/**
 * Отправить одну ошибку. Дубли и всё сверх лимита молча отбрасываются.
 *
 * @param kind откуда пришло: error, unhandledrejection, react, boundary…
 * @param extra дополнение к стеку (например, componentStack от React)
 */
export function reportError(error: unknown, kind = 'error', extra?: string | null): void {
  try {
    if (sent >= MAX_REPORTS) return
    const { message, stack } = describe(error)
    const full = extra ? `${stack ?? ''}\n${extra}`.trim() : stack
    const key = `${kind} ${message} ${full ?? ''}`
    if (seen.has(key)) return
    seen.add(key)
    sent += 1
    pending.push({
      kind: cut(kind, 32),
      message: cut(message || 'без описания', MAX_MESSAGE),
      stack: full ? cut(full, MAX_STACK) : null,
      url: cut(location.href, 512),
      userAgent: cut(navigator.userAgent, 512),
      at: Date.now() / 1000,
    })
    if (timer === null) timer = window.setTimeout(flush, BATCH_DELAY_MS)
  } catch {
    // см. выше: молчим
  }
}

/**
 * Полный сброс клиентского состояния: снять service worker, стереть его кеши,
 * забыть токен и перезагрузиться.
 *
 * Это единственный способ вылечить панель с телефона, когда битая оболочка уже
 * лежит в кеше воркера: обычное обновление страницы отдаёт её же, а снести
 * установленное PWA и поставить заново — не всегда достаточно.
 *
 * Тот же код продублирован в index.html как `window.__abResetCache`: там
 * импортировать нечего, а кнопка нужна именно тогда, когда бандл не доехал.
 */
export async function resetAndReload(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map((reg) => reg.unregister()))
    }
  } catch {
    // воркера может не быть вовсе — это не мешает остальному сбросу
  }
  try {
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // Cache API закрыт (приватный режим) — идём дальше
  }
  try {
    localStorage.removeItem('agentbox.token')
  } catch {
    // localStorage тоже может быть закрыт
  }
  location.reload()
}

/**
 * Поставить глобальные обработчики и забрать всё, что накопил инлайновый
 * скрипт из index.html. Зовётся один раз из main.tsx, до render.
 */
export function initErrorReporting(): void {
  if (started) return
  started = true
  try {
    // Ранние ошибки: они случились до того, как появился этот модуль, —
    // именно среди них живут «бандл не распарсился» и «чанк не доехал».
    const early = window.__abEarlyErrors
    if (Array.isArray(early)) {
      for (const item of early.splice(0, MAX_REPORTS)) {
        reportError(
          { message: item.message, stack: item.stack },
          item.kind || 'early',
        )
      }
    }

    window.addEventListener('error', (event) => {
      if (event.error) reportError(event.error, 'error')
      else reportError(event.message, 'error')
    })

    window.addEventListener('unhandledrejection', (event) => {
      reportError(event.reason, 'unhandledrejection')
    })

    // Вкладку могут закрыть раньше, чем истечёт BATCH_DELAY_MS.
    // pagehide, а не unload: в мобильных браузерах unload часто не приходит.
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush()
    })
  } catch {
    // без телеметрии панель работает, с упавшим main.tsx — нет
  }
}
